import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { DocFolderWithAccess, DocGrant } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

/**
 * Every folder's own explicit grants, keyed by folder id.
 *
 * `DocsRepository.listGrants` is per-folder. Resolving inheritance for one
 * selected folder (FR-DOC-8) needs every ancestor's grants too, and which
 * folder is an ancestor of which changes as the selection changes — so this
 * fetches the whole visible tree's grants once rather than re-fetching a
 * fresh ancestor chain on every click.
 */
export function useDocsGrants(
  folders: readonly DocFolderWithAccess[],
): UseQueryResult<Map<string, DocGrant[]>> {
  const data = useDataSource();
  const ids = folders.map((folder) => folder.id);

  return useQuery({
    queryKey: ["docs", "grants", ids.join(",")],
    queryFn: async () => {
      const entries = await Promise.all(
        folders.map(async (folder) => {
          const grants = await data.docs.listGrants(folder.id);
          return [folder.id, grants] as const;
        }),
      );
      return new Map(entries);
    },
    enabled: folders.length > 0,
  });
}
